import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollText, ChevronDown, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function TycGate() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const [gate, setGate] = useState(null);
  const [readEnd, setReadEnd] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const inApp = location.pathname.startsWith("/app");
  const isUser = !!user && user.role !== "admin";

  const check = useCallback(() => {
    if (!inApp || !isUser) { setGate(null); return; }
    api.get("/tyc/status").then((r) => {
      if (r.data.enabled && !r.data.accepted) {
        setGate((g) => (g && g.currentVersion === r.data.currentVersion ? g : (setReadEnd(false), setConfirmReject(false), setBusy(false), r.data)));
      } else setGate(null);
    }).catch(() => {});
  }, [inApp, isUser]);

  useEffect(() => { check(); }, [check]);
  useEffect(() => {
    window.addEventListener("tyc-required", check);
    return () => window.removeEventListener("tyc-required", check);
  }, [check]);

  useEffect(() => {
    if (!gate) return;
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 8) setReadEnd(true);
  }, [gate]);

  if (!gate || !inApp || !isUser) return null;

  const onScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setReadEnd(true);
  };

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/tyc/accept");
      window.location.reload();
    } catch {
      toast.error("No se pudo registrar la aceptación. Intentá de nuevo.");
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try { await api.post("/tyc/reject"); } catch { /* logout anyway */ }
    setGate(null);
    logout();
    nav("/", { replace: true });
    toast.error("Debés aceptar los Términos y Condiciones para usar la aplicación.");
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="tyc-gate">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div className="relative bg-white border border-border shadow-2xl w-full max-w-[660px] flex flex-col max-h-[90vh]">
        <div className="p-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <ScrollText size={20} className="text-slate-700" />
            <h2 className="font-head text-lg font-bold text-slate-900" data-testid="tyc-gate-title">
              Términos y Condiciones y Política de Privacidad
            </h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1.5">
            Para continuar usando la aplicación, leé y aceptá los términos.
          </p>
        </div>
        <div ref={scrollRef} onScroll={onScroll} data-testid="tyc-gate-content"
          className="flex-1 overflow-y-auto p-6 text-sm text-slate-700 leading-relaxed [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_a]:text-blue-600 [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: gate.content }} />
        <div className="p-5 border-t border-border bg-slate-50">
          {confirmReject ? (
            <div data-testid="tyc-reject-confirm">
              <p className="font-medium text-slate-900 text-sm">Rechazar los términos</p>
              <p className="text-sm text-muted-foreground mt-1">
                Si rechazás los Términos y Condiciones y la Política de Privacidad, se cerrará tu sesión y no podrás usar la aplicación.
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setConfirmReject(false)} disabled={busy}
                  data-testid="tyc-reject-back" className="rounded-none">Volver</Button>
                <Button onClick={reject} disabled={busy} data-testid="tyc-reject-confirm-btn"
                  className="rounded-none bg-red-600 hover:bg-red-700 text-white">Rechazar y salir</Button>
              </div>
            </div>
          ) : (
            <>
              {!readEnd ? (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-3" data-testid="tyc-scroll-hint">
                  <ChevronDown size={14} /> Desplazate hasta el final para habilitar la aceptación
                </p>
              ) : (
                <p className="text-xs text-emerald-700 flex items-center gap-1.5 mb-3" data-testid="tyc-read-complete">
                  <CheckCircle2 size={14} /> Leíste el documento completo
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmReject(true)} disabled={busy}
                  data-testid="tyc-reject-btn" className="rounded-none border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700">
                  Rechazar
                </Button>
                <Button onClick={accept} disabled={!readEnd || busy} data-testid="tyc-accept-btn"
                  className="rounded-none brand-bg text-white">
                  Acepto los Términos y Condiciones
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
